import { filter_data, get_overall_average } from "reports/Tag/calculations";
import { calculate_engagement_by_anchor } from "./caluclate_engagement";

// Utility function to check if a value is a valid number (not 'N/A' or other non-numeric values)
// This function filters out various forms of 'N/A' values that might appear in survey responses
// and ensures only valid numeric responses are used in calculations
function isValidNumericResponse(value) {
    return value !== null && 
           value !== undefined && 
           value !== 'N/A' && 
           value !== 'n/a' && 
           value !== 'NA' && 
           value !== 'na' && 
           typeof value === 'number' && 
           !isNaN(value);
}

// Utility function to parse JSON responses from GPT
function parseGPTJsonResponse(gptResponse) {
    try {
        // Remove markdown code block delimiters if they exist
        let cleanedResponse = gptResponse.trim();
        
        // Remove ```json and ``` markers
        if (cleanedResponse.startsWith('```json')) {
            cleanedResponse = cleanedResponse.replace(/^```json\s*/i, '');
        }
        if (cleanedResponse.startsWith('```')) {
            cleanedResponse = cleanedResponse.replace(/^```\s*/, '');
        }
        if (cleanedResponse.endsWith('```')) {
            cleanedResponse = cleanedResponse.replace(/\s*```$/, '');
        }
        
        // Parse the JSON
        const parsedJson = JSON.parse(cleanedResponse);
        return parsedJson;
    } catch (error) {
        console.error("Error parsing GPT JSON response:", error);
        console.error("Original response:", gptResponse);
        
        // Return a fallback structure if parsing fails
        return {
            overview: "Error parsing response",
            quotes: [],
            insights: "Unable to parse insights from response",
            conclusion: "Response parsing failed"
        };
    }
}

// OpenAI function extracted from CopilotContext - now returns token usage
async function callOpenAI4(userPrompt, onTokenUpdate = null) {
    try {
        const response = await fetch(
            "https://api.openai.com/v1/chat/completions",
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer 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`,
                },
                body: JSON.stringify((() => {
                    const wantsJson = /json/i.test(userPrompt);
                    const base = {
                        model: "gpt-4.1",
                        seed: 1,
                        messages: [
                            { role: "user", content: userPrompt },
                        ],
                        stream: false,
                    };
                    const bodyObj = wantsJson ? { ...base, response_format: { type: "json_object" } } : base;
                    return bodyObj;
                })()),
            }
        );

        if (!response.ok) {
            throw new Error(`OpenAI API responded with status ${response.status}`);
        }

        const data = await response.json();
        const assistantMessage = data.choices?.[0]?.message?.content || "";
        const usage = data.usage || {};
        
        const tokenInfo = {
            prompt_tokens: usage.prompt_tokens || 0,
            completion_tokens: usage.completion_tokens || 0,
            total_tokens: usage.total_tokens || 0
        };

        // Call the token update callback if provided
        if (onTokenUpdate) {
            onTokenUpdate(tokenInfo);
        }

        return {
            content: assistantMessage,
            tokens: tokenInfo
        };
    } catch (error) {
        console.error("Error calling OpenAI:", error);
        throw error;
    }
}

export async function calculateSummary(raw_data, core_data, debriefSchedules, anchorParentID, onTokenUpdate = null, selectedModel = "gpt-4.1", selectedAnchorIDs = null, combineAnchors = false) {
    const questions = core_data.questions;
    const outcomeQuestions = core_data.outcomeQuestions;

    // Initialize token tracking
    let totalTokenUsage = {
        prompt_tokens: 0,
        completion_tokens: 0,
        total_tokens: 0,
        call_count: 0
    };

    const updateTokens = (newTokens) => {
        totalTokenUsage.prompt_tokens += newTokens.prompt_tokens;
        totalTokenUsage.completion_tokens += newTokens.completion_tokens;
        totalTokenUsage.total_tokens += newTokens.total_tokens;
        totalTokenUsage.call_count += 1;
        
        // Notify the UI of token updates
        if (onTokenUpdate) {
            onTokenUpdate(totalTokenUsage);
        }
    };

    // Find the selected anchor parent by ID
    const selectedAnchorParent = core_data.categories[0].categories.find(cat => cat.id === anchorParentID);
    
    if (!selectedAnchorParent) {
        throw new Error(`Anchor parent with ID ${anchorParentID} not found`);
    }

    // Calculate overall scores across all data
    const overallLatestScore = get_overall_average(raw_data[0].responses);
    const overallLastScore = get_overall_average(raw_data[0].last);
    const overallChange = overallLatestScore - overallLastScore;

    // Get overall factor changes across all data
    const latestFactorScores = calculateAverageScoresPerFactor(raw_data[0].responses);
    const lastFactorScores = calculateAverageScoresPerFactor(raw_data[0].last);
    const overallFactorChanges = getChangeData(latestFactorScores, lastFactorScores, questions, raw_data[0].responses, raw_data[0].last);

    // Sort and get top scores overall and negative changes
    const sortedByLatestScore = overallFactorChanges.sort((a,b) => b.latest_score - a.latest_score);
    const topScores = sortedByLatestScore.filter(f => typeof f.latest_score === 'number').slice(0, 3);
    const sortedByChange = overallFactorChanges.sort((a,b) => b.score_change - a.score_change);
    let negativeChanges = sortedByChange.filter(f => f.score_change < 0).slice(-3).reverse();
    
    // If no negative changes, use the lowest scoring factors instead
    if (negativeChanges.length === 0) {
        negativeChanges = sortedByLatestScore
            .filter(f => typeof f.latest_score === 'number')
            .slice(-3)
            .reverse();
    }

    // Generate GPT summaries for top scores
    const topScoresOverall = [];
    for (const topScore of topScores) {
        if (typeof topScore.latest_score === 'number') {
            const feedbackSummaryPrompt = `
            Please analyze the following feedback data for a high-performing factor:
            
            **Factor:** ${topScore.factor_name}
            **Overall Score:** ${topScore.latest_score.toFixed(2)}
            
            **Latest Feedback:**
            ${topScore.latest_feedback || "No feedback provided"}
            
            Provide a 1-sentence summary explaining what makes this factor perform well based on the current feedback. Focus on the key strengths.
            
            Return only the 1-sentence summary, no additional formatting.
            `;
            
            const gptResult = await callOpenAI4(feedbackSummaryPrompt, updateTokens);
            
            topScoresOverall.push({
                factor_name: topScore.factor_name,
                latest_score: topScore.latest_score,
                last_score: topScore.last_score,
                feedback_summary: gptResult.content
            });
        }
    }

    const topNegativeChanges = [];
    for (const change of negativeChanges) {
        // Check if this is actually a negative change or just a low scoring factor
        const isNegativeChange = change.score_change < 0;
        
        const feedbackSummaryPrompt = isNegativeChange ? `
        Please analyze the following feedback data for a factor that has declined:
        
        **Factor:** ${change.factor_name}
        **Score Change:** ${change.score_change.toFixed(2)}
        
        **Latest Feedback:**
        ${change.latest_feedback || "No feedback provided"}
        
        **Previous Feedback:**
        ${change.last_feedback || "No feedback provided"}
        
        Provide a 1-sentence summary explaining the key reason for this negative change based on the feedback comparison. Focus on what specifically declined or became problematic.
        
        Return only the 1-sentence summary, no additional formatting.
        ` : `
        Please analyze the following feedback data for a low-performing factor:
        
        **Factor:** ${change.factor_name}
        **Current Score:** ${change.latest_score.toFixed(2)}
        
        **Latest Feedback:**
        ${change.latest_feedback || "No feedback provided"}
        
        Provide a 1-sentence summary explaining the key challenges or issues for this low-performing factor based on the current feedback. Focus on what specifically is problematic or needs improvement.
        
        Return only the 1-sentence summary, no additional formatting.
        `;
        
        const gptResult = await callOpenAI4(feedbackSummaryPrompt, updateTokens);
        
        topNegativeChanges.push({
            factor_name: change.factor_name,
            score_change: change.score_change,
            latest_score: change.latest_score,
            last_score: change.last_score,
            feedback_summary: gptResult.content
        });
    }

    // Collect anchor-specific data
    const anchorDataResults = [];

    // NOTE: Anchor participation rate calculation is temporarily disabled because
    // the employee categories object is now paginated and no longer returns
    // accurate totals for this computation. Once participation logic is updated
    // to handle pagination correctly, restore this call and related usage.
    //
    // const participationRate = calculate_engagement_by_anchor(
    //   debriefSchedules.culture_audit_reports.response,
    //   anchorParentID,
    //   core_data.categories,
    //   core_data.orgCategories
    // );
    //
    // console.log(participationRate, anchorParentID, debriefSchedules);
    const participationRate = [];


    // Determine which anchors to process (all by default, or filtered by selection)
    const optionsToProcess = Array.isArray(selectedAnchorIDs) && selectedAnchorIDs.length > 0
        ? selectedAnchorParent.options.filter(opt => selectedAnchorIDs.includes(opt.id))
        : selectedAnchorParent.options;
    // If combining anchors, compute a single combined report
    if (combineAnchors && optionsToProcess.length > 1) {
        const filterList = optionsToProcess.map(opt => ({ id: opt.id }));
        const orgName = (core_data && (core_data.orgName || core_data.organization?.name || core_data.org?.name || core_data.name)) || "Organization";
        const filteredData = filter_data(raw_data[0].responses, filterList);
        const lastFilteredData = filter_data(raw_data[0].last, filterList);
        
        const latestFactorScores = calculateAverageScoresPerFactor(filteredData);
        const lastFactorScores = calculateAverageScoresPerFactor(lastFilteredData);
        
        const outcomeScoresBySection = {};
        if (outcomeQuestions && outcomeQuestions.questions) {
            outcomeQuestions.questions.forEach(outcomeSection => {
                const latestSectionScores = calculateAverageScoresPerOutcome(filteredData, outcomeQuestions, outcomeSection.id);
                const lastSectionScores = calculateAverageScoresPerOutcome(lastFilteredData, outcomeQuestions, outcomeSection.id);
                
                outcomeScoresBySection[outcomeSection.id] = {
                    name: outcomeSection.name,
                    latest: latestSectionScores,
                    previous: lastSectionScores
                };
            });
        }
        
        const overallAverage = get_overall_average(filteredData);
        const lastOverallAverage = get_overall_average(lastFilteredData);
        const totalFeedbackCount = filteredData.length;
        const changeDataWithFeedback = getChangeData(latestFactorScores, lastFactorScores, questions, filteredData, lastFilteredData).sort((a,b)=>a.score_change-b.score_change);

        const prompt = `
        Please analyze the following data for organization name: ${orgName}
        ---
        **Scores**  
        - Latest: ${Math.round(overallAverage * 10 * 10) / 10}  
        - Previous: ${Math.round(lastOverallAverage * 10 * 10) / 10}

        **Factor Scores**
        ${changeDataWithFeedback.map(f=>`- ${f.factor_name}: ${f.latest_score} - \n\n Most recent Feedback ${f.latest_feedback}`).join("\n")}
        ---
        
        Provide JSON formatted response with the following structure:
        1. Overview of the feedback in 3-4 sentences. Focus on explaining the bigger themes that explain the overall scores across the combined anchors.
        2. 3 Quotes from Feedback that illustrate the biggest shifts or themes. Just include the quote text only in a list.
        3. Key takeaways (4 key takeaways). Focus on high level takeaways.
        4. Next steps - 3-4 sentences on what the overall feedback means and where to focus next.
        
          **Tone & Format**  
          - Keep it clear, concise, and professional.  
          - Never use markdown syntax for anything, just plain text.
          - Return STRICTLY VALID JSON (RFC 8259). Output JSON ONLY with no explanation before or after.
          - All keys and string values MUST use double quotes.

        **JSON Format**
        {
            "overview": "",
            "quotes": [],
            "key_takeaways": [],
            "next_steps": ""
        }
        `

        const gptResult = await callOpenAI4(prompt, updateTokens);
        const parsedGptAnalysis = parseGPTJsonResponse(gptResult.content);

        const outputData = {
            anchor_id: optionsToProcess.map(o=>o.id),
            anchor_name: orgName,
            overall_average: overallAverage,
            last_overall_average: lastOverallAverage,
            participation_rate: null,
            total_feedback_count: totalFeedbackCount,
            change_data: changeDataWithFeedback,
            gpt_analysis: parsedGptAnalysis,
            outcome_scores: outcomeScoresBySection
        }
        
        anchorDataResults.push(outputData);
    } else {
        // Iterate through each of the selected anchor options in the selected anchor parent
        for (const option of optionsToProcess) {
           
            const anchorID = option.id;
            const anchorName = option.name;
            const filteredData = filter_data(raw_data[0].responses, [{id:anchorID}]);
            const lastFilteredData = filter_data(raw_data[0].last, [{id:anchorID}]);
                
        // Get the anchor data
        const latestFactorScores = calculateAverageScoresPerFactor(filteredData);
        const lastFactorScores = calculateAverageScoresPerFactor(lastFilteredData);
        
        // Calculate outcome scores - now calculate per outcome section
        const outcomeScoresBySection = {};
        if (outcomeQuestions && outcomeQuestions.questions) {
            outcomeQuestions.questions.forEach(outcomeSection => {
                const latestSectionScores = calculateAverageScoresPerOutcome(filteredData, outcomeQuestions, outcomeSection.id);
                const lastSectionScores = calculateAverageScoresPerOutcome(lastFilteredData, outcomeQuestions, outcomeSection.id);
                
                outcomeScoresBySection[outcomeSection.id] = {
                    name: outcomeSection.name,
                    latest: latestSectionScores,
                    previous: lastSectionScores
                };
            });
        }
        
        const overallAverage = get_overall_average(filteredData);
        const lastOverallAverage = get_overall_average(lastFilteredData);
        
        // Count total feedback received for this anchor
        const totalFeedbackCount = filteredData.length;

        // Get the specific participation data for this anchor
        // NOTE: Temporarily disabled – participationRate is an empty array while
        // anchor participation calculation is turned off.
        // const currentAnchorParticipation = participationRate.find(p => p.anchor_id === anchorID);
        const currentAnchorParticipation = null;

        const changeDataWithFeedback = getChangeData(latestFactorScores, lastFactorScores, questions, filteredData, lastFilteredData).sort((a,b)=>a.score_change-b.score_change);

        const prompt = `
        Please analyze the following data for organization name:${anchorName}
        ---
        **Scores**  
        - Latest: ${Math.round(overallAverage * 10 * 10) / 10}  
        - Previous: ${Math.round(lastOverallAverage * 10 * 10) / 10}

        **Factor Scores**
        ${changeDataWithFeedback.map(f=>`- ${f.factor_name}: ${f.latest_score} - \n\n Most recent Feedback ${f.latest_feedback}`).join("\n")}
        ---
        
        Provide JSON formatted response with the following structure:
        1. Overview of the feedback in 3-4 sentences. Focus on explaining what the bigger themes are in the feedback that explain the overall scores.
        2. 3 Quotes from Feedback that illustrate the biggest shifts or themes. Just include the quote text only in a list.
        3. Key takeaways  (4 key takeways). Focus on bullet point, high level key feedback takeways.
        5. Next steps - 3-4 sentences on what the overall feedback means and where to focus next steps.
    
          **Tone & Format**  
          - Keep it clear, concise, and professional.  
          - Use **bold text** and headings for clarity.  
          - Aim for an **engaging, insight-focused** style that helps readers quickly grasp the main points.
          - Even though we are anchoring the analysis in complex culture factors, we want to communicate things in a simple way without the jargon.
          - Never use markdown syntax for anything, just plain text.
          - Never add "-" in front of any bullet points and never use markdown syntax for bullet points.
          - Return STRICTLY VALID JSON (RFC 8259). Output JSON ONLY with no explanation before or after.
          - All keys and string values MUST use double quotes. Arrays must contain JSON strings only.
          - Escape any internal double quotes in values as \\" or replace them with single quotes.
          - Do NOT include trailing commas, control characters, or line breaks inside values.

        **JSON Format**
        {
            "overview": "",
            "quotes": [],
            "key_takeaways": [],
            "next_steps": "",
        }
        `

        const gptResult = await callOpenAI4(prompt, updateTokens);
        const parsedGptAnalysis = parseGPTJsonResponse(gptResult.content);

        // Sort factors by latest_score to identify highest and lowest scoring factors
        const factorsByScore = [...changeDataWithFeedback].sort((a, b) => {
            // Handle cases where latest_score might be "No data"
            const scoreA = typeof a.latest_score === 'number' ? a.latest_score : 0;
            const scoreB = typeof b.latest_score === 'number' ? b.latest_score : 0;
            return scoreB - scoreA; // Descending order (highest first)
        });

        // Get top 3 highest and top 3 lowest scoring factors
        const top3Highest = factorsByScore.slice(0, 3);
        const top3Lowest = factorsByScore.slice(-3).reverse(); // Get last 3 and reverse to maintain lowest-first order
        
        // Combine and deduplicate in case there are fewer than 6 total factors
        const factorsForGPTAnalysis = new Set([...top3Highest, ...top3Lowest]);

        // Generate GPT analysis only for selected factors
        const changeDataWithGPTAnalysis = [];
        for (const item of changeDataWithFeedback) {
            
            let gptAnalysis = null;
            
            // Only call GPT for top/bottom scoring factors
            if (factorsForGPTAnalysis.has(item)) {
                const factorAnalysisPrompt = `
                Please analyze the following feedback data for this culture factor:
                
                **Factor:** ${item.factor_name}
                **Current Score:** ${item.latest_score.toFixed(2)}
                
                **Latest Feedback:**
                ${item.latest_feedback || "No feedback provided"}
                
                Provide a 1-sentence summary of the key theme or issue for this factor based on the current feedback. Focus on the main insights about what's happening with this factor right now.
                
                Return only the 1-sentence summary, no additional formatting.
                `;
                
                const gptResult = await callOpenAI4(factorAnalysisPrompt, updateTokens);
                gptAnalysis = gptResult.content;
            }
            
            changeDataWithGPTAnalysis.push({
                factor_name: item.factor_name,
                latest_score: item.latest_score,
                last_score: item.last_score,
                score_change: item.score_change,
                gpt_analysis: gptAnalysis
            });
        }

        const outputData = {
            anchor_id: anchorID,
            anchor_name: option.name,
            overall_average: overallAverage,
            last_overall_average: lastOverallAverage,
            participation_rate: currentAnchorParticipation,
            total_feedback_count: totalFeedbackCount,
            change_data: changeDataWithGPTAnalysis,
            gpt_analysis: parsedGptAnalysis,
            outcome_scores: outcomeScoresBySection
        }
        
        anchorDataResults.push(outputData);
        }
    }

    // Calculate final costs
    const inputCost = (totalTokenUsage.prompt_tokens / 1000000) * 2;
    const outputCost = (totalTokenUsage.completion_tokens / 1000000) * 8;
    const totalCost = inputCost + outputCost;

    // Return structured data as requested
    return {
        overall_summary: {
            overall_average: overallLatestScore,
            overall_change: overallChange,
            previous_average: overallLastScore
        },
        top_scores_overall: topScoresOverall,
        top_negative_changes: topNegativeChanges,
        anchor_data: anchorDataResults,
        selected_anchor_parent: {
            id: selectedAnchorParent.id,
            name: selectedAnchorParent.name
        },
        token_usage: {
            ...totalTokenUsage,
            input_cost: inputCost,
            output_cost: outputCost,
            total_cost: totalCost
        }
    };
}




/**
 * Calculate the average scores per culture factor.
 * Each entry in data is expected to have a "questions" array.
 * We use a composite key "factor_dimension" (e.g. "0_1") to track each factor.
 */
function calculateAverageScoresPerFactor(data) {
  console.log(data)
    const factorScores = {};
    data.forEach(entry => {
      const questions = entry.questions || [];
      questions.forEach(question => {
        const factor = question.factor;
        const dimension = question.id; // assuming "id" represents the dimension id
        const response = question.response;
        // Only process if we have valid numeric response (filter out 'N/A' values)
        if (factor != null && dimension != null && isValidNumericResponse(response)) {
          const key = `${factor}_${dimension}`;
          if (factorScores[key]) {
            factorScores[key].sum += response;
            factorScores[key].count += 1;
          } else {
            factorScores[key] = { sum: response, count: 1, factor, dimension };
          }
        }
      });
    });
    // Calculate averages for each factor key
    const averages = {};
    Object.keys(factorScores).forEach(key => {
      // Handle case where all responses were 'N/A' (count would be 0)
      if (factorScores[key].count > 0) {
        averages[key] = factorScores[key].sum / factorScores[key].count;
      } else {
        averages[key] = "No data"; // or could be null/undefined
      }
    });
    return averages;
  }

/**
 * Calculate the average scores and high score percentage for employee outcomes.
 * Uses the exact same method as heatmap calculations for consistency.
 * @param {Array} data - Array of response data
 * @param {Object} outcomeQuestions - Outcome questions structure
 * @param {string} selectedOutcomeId - ID of the specific outcome section to process
 */
function calculateAverageScoresPerOutcome(data, outcomeQuestions, selectedOutcomeId = null) {
    const results = {};
    
    if (!outcomeQuestions || !outcomeQuestions.questions) {
        return results;
    }
    
    // Filter outcome questions by selectedOutcomeId if provided (same as heatmap)
    const questionsToProcess = selectedOutcomeId 
        ? outcomeQuestions.questions.filter(question => String(question.id) === String(selectedOutcomeId))
        : outcomeQuestions.questions;
    
    questionsToProcess.forEach((question) => {
        let name = question.name;
        let filtered_responses = data
            .map((entry) =>
                entry?.employee_outcomes?.responses
                    ?.filter((i) => {
                        return (
                            i.q == question.id &&
                            (i?.clicked === true || i?.clicked === undefined)
                        );
                    })
                    .map((i) => i?.response)
            )
            .flat()
            .filter((f) => !isNaN(f) && f !== undefined && f !== null);

        if (filtered_responses.length > 0) {
            const sum = filtered_responses.reduce((acc, val) => acc + val, 0);
            const average = sum / filtered_responses.length;
            
            // Calculate percentage of responses that are 7 or above
            const highScores = filtered_responses.filter(response => response >= 7);
            const highScorePercentage = (highScores.length / filtered_responses.length) * 100;
            
            results[question.id] = {
                outcome_name: name,
                average_score: average,
                high_score_percentage: highScorePercentage,
                total_responses: filtered_responses.length
            };
        } else {
            results[question.id] = {
                outcome_name: name,
                average_score: "No data",
                high_score_percentage: 0,
                total_responses: 0
            };
        }
    });
    
    return results;
}

  function filterFeedback(responses, factor, dimension) {
    if (!responses || !Array.isArray(responses)) return [];
    
    const feedbackList = [];
    responses.forEach(entry => {
      const questions = entry.feedback || [];
      questions.forEach(question => {
        if (question.factor === factor && question.id === dimension && question.feedback) {
          feedbackList.push({
            feedback: question.feedback,
            question: question.question || "",
            follow_up: question.follow_up || "",
            follow_up_question: question.follow_up_question || "",
            prompt_question: question.prompt_question || ""
          });
        }
      });
    });
    
    return feedbackList;
  }

  function getFactorFeedback(factorTuple, responses, options = {}) {
    if (!factorTuple) return "";
    const [factor, dimension] = factorTuple;
    
    // Filter the responses
    const feedbackList = filterFeedback(responses, factor, dimension);
    if (!feedbackList.length) return "";
    
    // Depending on options, choose a formatting strategy. For now, we use one format.
    return formatFeedback(feedbackList, options);
  }
  
  function formatFeedback(feedbackList, options = {}) {
    if (!feedbackList.length) return "";
    
    const { headerName } = options;
    let formatted = headerName
      ? `### Feedback from ${headerName} responses:\n\n`
      : "### Feedback from responses:\n\n";
    
    feedbackList.forEach(item => {
      // If prompt_question exists, handle it differently
      if (item.prompt_question) {
        formatted += "Follow up: " + item.prompt_question + "\n";
        formatted += "Response: " + (item.feedback || "") + "\n";
        formatted += "\n";
        return;
      }
      
      if (item.feedback) {
        formatted += "Question asked: " + (item.question || "") + "\n";
        formatted += "Response: " + item.feedback + "\n";
      }
      
      if (item.follow_up && item.follow_up_question) {
        formatted += "Follow up: " + item.follow_up_question + "\n";
        formatted += "Response: " + item.follow_up + "\n";
      }
      
      formatted += "\n";
    });
    
    formatted += "### END OF FEEDBACK \n\n";
    return formatted;
  }

  function getFactorName(questionsStructure, factorTuple) {
    if (!factorTuple) return null;
    const [factorId, dimensionId] = factorTuple;
    const dimensions = questionsStructure.dimensions || [];
    // Find the dimension whose id equals dimensionId+1 (adjust if necessary)
    const dimension = dimensions.find(dim => dim.id === dimensionId + 1);
    if (!dimension) return null;
    const factor = dimension.factors.find(fact => fact.id === factorId + 1);
    if (!factor) return null;
    return factor.title;
  }

  function getChangeData(latestFactorScores, lastFactorScores, questions, filteredData = null, lastFilteredData = null){

    // Get the union of all factor keys (composite keys: "factor_dimension")
    const allFactorKeys = new Set([
        ...Object.keys(latestFactorScores),
        ...Object.keys(lastFactorScores)
      ]);

      const factorChanges = [];

       // Process each culture factor sequentially
       for (let key of allFactorKeys) {
        const parts = key.split("_");
        const factorId = parseInt(parts[0], 10);
        const dimensionId = parseInt(parts[1], 10);
    
        const factorName = getFactorName(questions, [factorId, dimensionId]);
        const latestScore =
          latestFactorScores[key] !== undefined
            ? latestFactorScores[key]
            : "No data";
        const lastScore =
          lastFactorScores[key] !== undefined ? lastFactorScores[key] : "No data";

        // Track the numeric score change
        const scoreChange =
          typeof latestScore === "number" && typeof lastScore === "number"
            ? latestScore - lastScore
            : 0;

        const latestFeedback = filteredData ? getFactorFeedback(
            [factorId, dimensionId],
            filteredData
          ) : "";
          const lastFeedback = lastFilteredData ? getFactorFeedback(
            [factorId, dimensionId],
            lastFilteredData
          ) : "";

        factorChanges.push({
          factor_id: key,
          factor_name: factorName,
          latest_score: latestScore,
          last_score: lastScore,
          score_change: scoreChange,
          latest_feedback: latestFeedback,
          last_feedback: lastFeedback
        });
    }

    return factorChanges;
  }